import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import {
  IconArrowLeft,
  IconEye,
  IconMapPin,
  IconSave,
  IconTrash,
} from '../../icons';
import Button from '../core/Button';
import IconButton from '../core/IconButton';
import Input from '../core/Input';
import { Dropdown, OptionsType } from '../core/Dropdown';
import Tooltip from '../Tooltip';
import { DialogWindow } from '../DialogWindow';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useContractors } from '../../hooks/useContractors';
import PeriodicityControl from '../CheckInPlanManagement/PeriodicityControl';
import { PlanValidityResults } from './RecurringWOPlanManagementDialog';
import Permissions from '../auth/Permissions';
import { useWorkOrderInstructions } from '../../hooks/useWorkOrderInstructions';
import TagSelector from '../Tags/TagSelector';
import { dateFormat } from '@/helpers/datetime_helpers';
import { DateTime } from 'luxon';

const messages = defineMessages({
  choose_contractor: {
    id: 'recurring_plan_dialog.choose_contractor',
    defaultMessage: 'Choose contractor (optional)',
  },
  workInstructions: {
    id: 'recurring_plan_dialog.work_instructions',
    defaultMessage: 'Enter work instructions here...',
  },
  choose_filter_set: {
    id: 'recurring_plan_dialog.choose_filter_set',
    defaultMessage: 'Choose filter set',
  },
  filter_set_results_preview: {
    id: 'recurring_plan_dialog.filter_set_results_preview',
    defaultMessage: 'Filter set results preview',
  },
  preview_filter_set_results: {
    id: 'recurring_plan_dialog.preview_filter_set_results',
    defaultMessage: 'Preview filter set results',
  },
  no_contractor: {
    id: 'edit_workorder_dialog.no_contractor',
    defaultMessage: 'No contractor',
  },
  no_templates: {
    id: 'no_templates_created',
    defaultMessage: 'No templates created',
  },
});

interface EditRecurringWOPlanFormProps {
  formData: Partial<RecurringPlan>;
  validity: PlanValidityResults;
  onChange: (formData: Partial<RecurringPlan>) => void;
  isProcessing: boolean;
  onCancel: () => void;
  onSave: () => void;
  filterSets: FilterSet[];
  onRemove: () => void;
  filteredEntityNames: string[];
}

const EditRecurringWOPlanForm: FC<EditRecurringWOPlanFormProps> = ({
  formData,
  filterSets,
  validity,
  onSave,
  onCancel,
  isProcessing,
  onRemove,
  filteredEntityNames,
  onChange,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const { list: contractors } = useContractors();
  const { list: instructions } = useWorkOrderInstructions();

  const [showEntityPreview, setShowEntityPreview] = useState<boolean>(false);

  const instructionsOptions = useMemo<OptionsType[]>(
    () => instructions.map((i) => ({ id: i.id, label: i.title })),
    [instructions],
  );

  const toggleEntityPreview = useCallback(() => {
    setShowEntityPreview(!showEntityPreview);
  }, [showEntityPreview]);

  const filterSetOptions = useMemo<OptionsType[]>(() => {
    return filterSets
      .map(({ id, name }) => ({
        id,
        label: name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
  }, [filterSets]);

  const contractorOptions = useMemo<OptionsType[]>(
    () => [
      { id: '', label: intl.formatMessage(messages.no_contractor) },
      ...contractors.map(({ id, name }) => ({ id, label: name })),
    ],
    [contractors, intl],
  );

  const selectedContractorOptions = useMemo(
    () =>
      (formData.contractors || []).map((id) => ({
        id,
        label: contractors.find((c) => c.id === id)?.name || id,
      })),
    [contractors, formData.contractors],
  );

  const isValid = useMemo(() => Object.keys(validity).length === 0, [validity]);

  const onLoadInstruction = useCallback(
    (instructionId: string) => {
      const instruction = instructions.find((i) => i.id === instructionId);
      if (!instruction) {
        return;
      }
      const texts = instruction.texts.join(`\n`);
      const tags = Array.from(
        new Set([...(formData.tags || []), ...(instruction.tags || [])]),
      );
      onChange({
        description: formData.description
          ? formData.description + `\n` + texts
          : texts,
        tags,
      });
    },
    [instructions, formData.description, onChange, formData.tags],
  );

  return (
    <>
      <DialogWindow
        open={showEntityPreview}
        onOpenChange={(open) => !open && toggleEntityPreview()}
        title={intl.formatMessage(messages.filter_set_results_preview)}
        className="max-w-[600px]"
        contentClassName="px-6 pb-5 overflow-hidden"
        content={
          <div className="flex flex-col">
            <p className="ml-5 text-[10px] uppercase leading-4 tracking-[1.5px]">
              <FormattedMessage
                id="recurring_plan_dialog.stops"
                defaultMessage="Stops"
              />
            </p>
            {formData.filterset_id && filteredEntityNames.length > 0 && (
              <p className="ml-5 text-base font-semibold leading-6 text-text-800">
                <FormattedMessage
                  id="recurring_plan_dialog.stop_list_explanation"
                  defaultMessage="These are stops resolved by the chosen filter set right now. Execution of this rule later may result in different results!{br}For more information please see the guide."
                  values={{
                    br: <br />,
                  }}
                />
              </p>
            )}
            {formData.filterset_id && filteredEntityNames.length === 0 && (
              <p className="ml-5 text-base font-semibold leading-6 text-text-800">
                <FormattedMessage
                  id="recurring_plan_dialog.stop_list_explanation_no_stops"
                  defaultMessage="Your chosen filter set does not currently resolve to any stops.{br}For more information please see the guide."
                  values={{
                    br: <br />,
                  }}
                />
              </p>
            )}
            {!formData.filterset_id && (
              <p className="ml-5 text-base font-semibold leading-6 text-text-800">
                <FormattedMessage
                  id="recurring_plan_dialog.stop_list_explanation_no_filter_set"
                  defaultMessage="Please select a filter set to preview the stops that will be included in the plan."
                  values={{
                    br: <br />,
                  }}
                />
              </p>
            )}
            <ul className="py-2">
              {filteredEntityNames.map((entityName, i) => {
                return (
                  <li
                    key={entityName + i}
                    className="flex items-center gap-5 px-4 py-1"
                  >
                    <IconMapPin className="size-6 text-yellow-500" />
                    <span className="flex-auto text-base leading-6">
                      {entityName}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        }
      />
      <div ref={ref} className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">
        <div className="flex flex-row items-stretch gap-5">
          <div className="h-[500px] grow-[2] basis-0">
            <p className="mt-2 text-[10px] uppercase leading-4 tracking-[1.5px]">
              <FormattedMessage
                id="check_in_plan_management.title_input_label"
                defaultMessage="Title"
              />
            </p>
            <div className="mb-2">
              <Input
                type="text"
                value={formData.title || ''}
                onChange={(ev) => onChange({ title: ev.target.value })}
              />
            </div>
            <Permissions oneOf={['role:admin']}>
              <p className="text-[10px] uppercase leading-4 tracking-[1.5px]">
                <FormattedMessage
                  id="recurring_plan_dialog.contractors_input_label"
                  defaultMessage="Contractors (optional)"
                />
              </p>
              <Dropdown
                id="recurring-plan-contractors"
                multiSelect
                placeholder={intl.formatMessage(messages.choose_contractor)}
                options={contractorOptions}
                value={selectedContractorOptions}
                onValueChange={(values) => {
                  const ids = values.map((v) => v.id);
                  onChange({
                    contractors: ids.includes('') ? [] : ids,
                  });
                }}
                containerRef={ref}
              />
            </Permissions>
            <p className="mt-2 text-[10px] uppercase leading-4 tracking-[1.5px]">
              <FormattedMessage
                id="recurring_plan_dialog.description_input_label"
                defaultMessage="Work instructions"
              />
            </p>
            <Input
              type="multiline"
              rows={13}
              value={formData.description}
              onChange={(ev) => onChange({ description: ev.target.value })}
              placeholder={intl.formatMessage(messages.workInstructions)}
            />
            <div className="mt-2 flex justify-between">
              <Button
                variant="outline"
                onClick={() => onChange({ description: '' })}
              >
                <FormattedMessage
                  id="recurring_plan_dialog.clear_instructions"
                  defaultMessage="Clear"
                />
              </Button>
              <div className="w-72">
                <Dropdown
                  id="instructions-dropdown"
                  options={instructionsOptions}
                  value={{
                    id: '',
                    label: intl.formatMessage({
                      id: 'edit_workorder_dialog.load_template',
                      defaultMessage: 'Load template',
                    }),
                  }}
                  onValueChange={(value) => onLoadInstruction(value.id)}
                  containerRef={ref}
                />
              </div>
            </div>
          </div>
          <div className="grow basis-0">
            <p className="mt-2 text-[10px] uppercase leading-4 tracking-[1.5px]">
              <FormattedMessage
                id="recurring_plan_dialog.valid_from"
                defaultMessage="Valid from"
              />
            </p>
            <Tooltip
              label={
                <FormattedMessage
                  id="recurring_plan_dialog.valid_from_input_tooltip"
                  defaultMessage="The date from which this rule will be active."
                />
              }
            >
              <Input
                type="date"
                value={formData.valid_from?.toFormat(dateFormat) || ''}
                onChange={(ev) =>
                  onChange({
                    valid_from: ev.target.value
                      ? DateTime.fromFormat(ev.target.value, dateFormat)
                      : undefined,
                  })
                }
              />
            </Tooltip>
            <p className="mt-2 text-[10px] uppercase leading-4 tracking-[1.5px]">
              <FormattedMessage
                id="recurring_plan_dialog.valid_to"
                defaultMessage="Valid until (optional)"
              />
            </p>
            <Tooltip
              side="top"
              label={
                <FormattedMessage
                  id="recurring_plan_dialog.valid_to_input_tooltip"
                  defaultMessage="The date at which this rule will be deactivated."
                />
              }
            >
              <Input
                type="date"
                value={formData.valid_to?.toFormat(dateFormat) || ''}
                onChange={(ev) =>
                  onChange({
                    valid_to: ev.target.value
                      ? DateTime.fromFormat(ev.target.value, dateFormat)
                      : undefined,
                  })
                }
              />
            </Tooltip>
            <p className="mt-2 text-[10px] uppercase leading-4 tracking-[1.5px]">
              <FormattedMessage
                id="recurring_plan_dialog.creation_offset_input_label"
                defaultMessage="Creation offset"
              />
            </p>

            <Tooltip
              side="top"
              label={
                <FormattedMessage
                  id="recurring_plan_dialog.creation_offset_input_tooltip"
                  defaultMessage="The number of days before occurrence the plan will be executed and work orders created."
                />
              }
            >
              <div className="mb-2">
                <Input
                  type="number"
                  value={formData.creation_offset || ''}
                  onChange={(ev) => {
                    const v = ev.target.value
                      ? parseInt(ev.target.value, 10)
                      : undefined;
                    if (!v || !isFinite(v)) {
                      onChange({
                        creation_offset: undefined,
                      });
                      return;
                    }
                    onChange({
                      creation_offset: v,
                    });
                  }}
                />
              </div>
            </Tooltip>

            <PeriodicityControl
              onChange={(periodicity) => onChange({ periodicity })}
              periodicity={
                formData.periodicity || { type: 'yearly', occurrences: [] }
              }
              containerRef={ref}
            />
            <p className="mt-2 text-[10px] uppercase leading-4 tracking-[1.5px]">
              <FormattedMessage
                id="recurring_plan_dialog.filter_set_input_label"
                defaultMessage="Filter set"
              />
            </p>
            <div className="flex gap-2">
              <Dropdown
                id="filter-set-dropdown"
                options={filterSetOptions}
                value={{
                  id: formData.filterset_id || '',
                  label:
                    filterSets.find((f) => f.id === formData.filterset_id)
                      ?.name || '',
                }}
                onValueChange={(value) => onChange({ filterset_id: value.id })}
              />
              <Tooltip
                label={
                  <FormattedMessage
                    id="recurring_plan_dialog.preview_filter_set_results"
                    defaultMessage="Preview filter set results"
                  />
                }
              >
                <IconButton
                  variant="ghost"
                  icon={<IconEye />}
                  aria-label={intl.formatMessage(
                    messages.preview_filter_set_results,
                  )}
                  onClick={toggleEntityPreview}
                />
              </Tooltip>
            </div>
            <p className="pt-2 text-[10px] uppercase leading-4 tracking-[1.5px]">
              <FormattedMessage
                id="recurring_plan_dialog.tags_input_label"
                defaultMessage="Tags"
              />
            </p>
            <TagSelector
              selectedTagIds={formData.tags || []}
              onChange={(tagIds) => onChange({ tags: tagIds })}
            />
          </div>
        </div>
      </div>
      <div className="shrink-0 p-2">
        <div className="flex w-full flex-row items-center gap-2">
          <Button
            variant="ghost"
            startIcon={<IconArrowLeft />}
            disabled={isProcessing}
            onClick={onCancel}
          >
            <FormattedMessage
              id="recurring_plan_dialog.back_to_existing_plans"
              defaultMessage="Back to existing plans"
            />
          </Button>
          {formData.id ? (
            <div className="flex grow justify-center">
              <Tooltip
                side="top"
                label={
                  <FormattedMessage
                    id="recurring_plan_dialog.remove_tooltip"
                    defaultMessage="Deletes the selected recurring work order plan."
                  />
                }
              >
                <Button
                  variant="outline"
                  color="red"
                  loading={isProcessing}
                  onClick={onRemove}
                  endIcon={<IconTrash />}
                >
                  <FormattedMessage id="remove" defaultMessage="Remove" />
                </Button>
              </Tooltip>
            </div>
          ) : (
            <div className="grow" />
          )}

          <Tooltip
            side="top"
            label={
              isValid ? (
                <FormattedMessage
                  id="recurring_plan_dialog.save_tooltip"
                  defaultMessage="Save recurring work order plan"
                />
              ) : (
                <span className="block whitespace-pre-wrap">
                  <FormattedMessage
                    id="recurring_plan_dialog.save_disabled_tooltip"
                    defaultMessage="Save recurring work order plan. Disabled because some details are missing or incorrect:"
                  />
                  {validity.creation_offset &&
                    `\n- ` + validity.creation_offset}
                  {validity.description && `\n- ` + validity.description}
                  {validity.title && `\n- ` + validity.title}
                  {validity.valid_from && `\n- ` + validity.valid_from}
                  {validity.periodicity && `\n- ` + validity.periodicity}
                  {validity.filterset_id && `\n- ` + validity.filterset_id}
                </span>
              )
            }
          >
            <span className="inline-flex">
              <Button
                variant="filled"
                loading={isProcessing}
                disabled={!isValid}
                onClick={onSave}
                startIcon={<IconSave />}
              >
                {formData.id ? (
                  <FormattedMessage
                    id="recurring_plan_dialog.save_recurring_work_order_plan"
                    defaultMessage="Save recurring work order plan"
                  />
                ) : (
                  <FormattedMessage
                    id="recurring_plan_dialog.create_recurring_work_order_plan"
                    defaultMessage="Create recurring work order plan"
                  />
                )}
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
    </>
  );
};

export default EditRecurringWOPlanForm;
